/** Per-branch summary card for the centralized owner dashboard (ML-04). */
import type { types } from '@gym/shared';
import { MapPin } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Money, StatusPill } from '../../components/ui.js';

function Stat({ label, value }: { label: ReactNode; value: ReactNode }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className="m-0 text-sm font-semibold text-fg tnum">{value}</dd>
    </div>
  );
}

export function LocationCard({
  location,
}: {
  location: types.MultiLocationLocationSummary;
}): ReactNode {
  const { t } = useTranslation();
  const capacityLabel =
    location.capacity != null
      ? `${location.live_occupancy} / ${location.capacity}`
      : `${location.live_occupancy}`;

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/15 text-primary">
            <MapPin className="h-4 w-4" aria-hidden />
          </span>
          <h3 className="font-display text-base font-semibold text-fg">{location.location_name}</h3>
        </div>
        <StatusPill
          tone={location.hardware_online ? 'success' : 'muted'}
          status={location.hardware_online ? 'active' : 'pending'}
          label={
            location.hardware_online
              ? t('multilocation.hardwareOnline')
              : t('multilocation.hardwareOffline')
          }
        />
      </div>
      <dl className="grid gap-2.5">
        <Stat label={t('multilocation.liveOccupancy')} value={capacityLabel} />
        <Stat label={t('multilocation.checkinsToday')} value={location.checkins_today} />
        <Stat label={t('multilocation.revenue')} value={<Money minor={location.gross_revenue} />} />
      </dl>
    </Card>
  );
}
